<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import ConfigForm from '../components/ConfigForm.vue';
import { createConfigDraft } from '../components/configDraft';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TransferTab from './TransferTab.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const configState = createConfigDraft(props.bot);
const { draft, setValue } = configState;

// Customer-facing handoff settings. Field definitions mirror HandoffTab.vue;
// only `section` differs. The safety-side fields live in SafetyTab.vue.
// The main card is what most clients need: the built-in keywords already
// transfer "chat cs" and friends, so everything else sits in collapsed cards.
const fields = [
  {
    path: 'handoff.message',
    section: 'TRANSFER',
    label: 'AI_AGENT.HANDOFF.FIELDS.MESSAGE',
    type: 'textarea',
  },
  {
    path: 'handoff.force_after_messages',
    section: 'TRANSFER',
    label: 'AI_AGENT.HANDOFF.FIELDS.FORCE_AFTER_MESSAGES',
    type: 'number',
    help: 'AI_AGENT.HANDOFF.FIELDS.FORCE_AFTER_MESSAGES_HELP',
  },
  {
    path: 'handoff.keyword',
    section: 'TRANSFER_MESSAGES',
    label: 'AI_AGENT.HANDOFF.FIELDS.KEYWORD',
    type: 'text',
    help: 'AI_AGENT.HANDOFF.FIELDS.KEYWORD_HELP',
  },
  {
    path: 'handoff.summary_enabled',
    section: 'TRANSFER_MESSAGES',
    label: 'AI_AGENT.HANDOFF.FIELDS.SUMMARY_ENABLED',
    type: 'switch',
  },
  {
    path: 'handoff.pickup_timeout_minutes',
    section: 'PICKUP',
    label: 'AI_AGENT.HANDOFF.FIELDS.PICKUP_TIMEOUT_MINUTES',
    type: 'number',
    help: 'AI_AGENT.HANDOFF.FIELDS.PICKUP_TIMEOUT_MINUTES_HELP',
  },
  {
    path: 'handoff.pickup_message',
    section: 'PICKUP',
    label: 'AI_AGENT.HANDOFF.FIELDS.PICKUP_MESSAGE',
    type: 'textarea',
    help: 'AI_AGENT.HANDOFF.FIELDS.PICKUP_MESSAGE_HELP',
  },
  {
    path: 'handoff.on_unanswered',
    section: 'AUTO_TRANSFER',
    label: 'AI_AGENT.HANDOFF.FIELDS.ON_UNANSWERED',
    type: 'switch',
  },
  {
    path: 'handoff.on_repeat',
    section: 'AUTO_TRANSFER',
    label: 'AI_AGENT.HANDOFF.FIELDS.ON_REPEAT',
    type: 'switch',
  },
  {
    path: 'handoff.repeat_threshold',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.REPEAT_THRESHOLD',
    type: 'number',
  },
  {
    path: 'handoff.keyword_fuzzy',
    section: 'ADVANCED',
    label: 'AI_AGENT.HANDOFF.FIELDS.KEYWORD_FUZZY',
    type: 'switch',
  },
];

// Built lazily: the account locale may load after this tab is set up.
const sections = computed(() => [
  {
    key: 'TRANSFER',
    icon: 'i-lucide-headset',
    title: t('AI_AGENT.TRANSFER.HUB.TITLE'),
    description: t('AI_AGENT.TRANSFER.HUB.DESCRIPTION'),
  },
  {
    key: 'TRANSFER_MESSAGES',
    icon: 'i-lucide-life-buoy',
    title: t('AI_AGENT.HANDOFF.SECTIONS.TRANSFER_MESSAGES'),
    description: t('AI_AGENT.HANDOFF.SECTIONS.TRANSFER_MESSAGES_HELP'),
    collapsible: true,
  },
  {
    key: 'PICKUP',
    icon: 'i-lucide-alarm-clock',
    title: t('AI_AGENT.HANDOFF.SECTIONS.PICKUP'),
    description: t('AI_AGENT.HANDOFF.SECTIONS.PICKUP_HELP'),
    collapsible: true,
  },
  {
    key: 'AUTO_TRANSFER',
    icon: 'i-lucide-arrow-right-left',
    title: t('AI_AGENT.HANDOFF.SECTIONS.AUTO_TRANSFER'),
    description: t('AI_AGENT.HANDOFF.SECTIONS.AUTO_TRANSFER_HELP'),
    collapsible: true,
  },
  {
    key: 'ADVANCED',
    icon: 'i-lucide-sliders-horizontal',
    title: t('AI_AGENT.STUDIO.ADVANCED'),
    description: t('AI_AGENT.STUDIO.ADVANCED_HELP'),
    collapsible: true,
  },
]);

// Unset on bots saved before the switch existed: the server default is on.
const defaultKeywordsEnabled = computed({
  get: () => draft.handoff?.default_keywords_enabled !== false,
  set: value => setValue({ path: 'handoff.default_keywords_enabled' }, value),
});

const teams = useMapGetter('teams/getTeams');

const teamOptions = computed(() => [
  { value: '', label: t('AI_AGENT.HANDOFF.NO_TEAM') },
  ...teams.value.map(team => ({ value: team.id, label: team.name })),
]);

const teamId = computed({
  get: () => draft.handoff?.team_id ?? '',
  set: value =>
    setValue({ path: 'handoff.team_id' }, value === '' ? null : Number(value)),
});

// Instruction patterns with a live regex test box
const ensurePatterns = () => {
  if (!Array.isArray(draft.handoff?.instruction_patterns)) {
    setValue({ path: 'handoff.instruction_patterns' }, []);
  }
  return draft.handoff.instruction_patterns;
};

const patterns = computed(() =>
  Array.isArray(draft.handoff?.instruction_patterns)
    ? draft.handoff.instruction_patterns
    : []
);

const newPattern = ref('');
const testText = ref('');

const patternState = pattern => {
  try {
    const regex = new RegExp(pattern);
    return { valid: true, matches: regex.test(testText.value) };
  } catch (error) {
    return { valid: false, matches: false };
  }
};

const addPattern = () => {
  const pattern = newPattern.value.trim();
  if (!pattern) return;
  ensurePatterns().push(pattern);
  newPattern.value = '';
};

const removePattern = index => {
  patterns.value.splice(index, 1);
};

onMounted(() => {
  store.dispatch('teams/get');
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <ConfigForm
      :bot="bot"
      :state="configState"
      :fields="fields"
      :sections="sections"
    >
      <template #section-TRANSFER>
        <div
          class="flex items-start justify-between order-first gap-4 px-3 py-2.5 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <div class="flex flex-col gap-0.5 min-w-0">
            <span class="text-label text-n-slate-12">
              {{ $t('AI_AGENT.TRANSFER.HUB.DEFAULT_KEYWORDS') }}
            </span>
            <span class="text-label-small text-n-slate-10">
              {{ $t('AI_AGENT.TRANSFER.HUB.DEFAULT_KEYWORDS_HELP') }}
            </span>
          </div>
          <Switch
            v-model="defaultKeywordsEnabled"
            class="mt-1"
            data-testid="field-handoff.default_keywords_enabled"
          />
        </div>
      </template>

      <template #section-TRANSFER_MESSAGES>
        <div class="flex flex-col gap-1.5 pt-4 border-t border-n-weak">
          <span class="text-label text-n-slate-12">
            {{ $t('AI_AGENT.HANDOFF.FIELDS.TEAM') }}
          </span>
          <Select
            v-model="teamId"
            :options="teamOptions"
            data-testid="field-handoff.team_id"
          />
          <p class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.HANDOFF.FIELDS.TEAM_HELP') }}
          </p>
        </div>
      </template>

      <template #section-ADVANCED>
        <div class="flex flex-col gap-3 pt-4 border-t border-n-weak">
          <div class="flex flex-col gap-0.5">
            <h4 class="text-heading-3 text-n-slate-12">
              {{ $t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.HEADING') }}
            </h4>
            <p class="text-label-small text-n-slate-10">
              {{ $t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.HELP') }}
            </p>
          </div>
          <ul v-if="patterns.length" class="flex flex-col gap-1.5">
            <li
              v-for="(pattern, index) in patterns"
              :key="index"
              class="flex items-center gap-2 py-1 pl-3 pr-1 rounded-lg bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
            >
              <code class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
                {{ pattern }}
              </code>
              <span
                v-if="!patternState(pattern).valid"
                class="px-2 py-0.5 rounded-full text-label-small bg-n-ruby-9/10 text-n-ruby-11"
              >
                {{ $t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.INVALID') }}
              </span>
              <span
                v-else-if="testText"
                class="px-2 py-0.5 rounded-full text-label-small"
                :class="
                  patternState(pattern).matches
                    ? 'bg-n-teal-9/10 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                {{
                  patternState(pattern).matches
                    ? $t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.MATCHES')
                    : $t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.NO_MATCH')
                }}
              </span>
              <Button
                type="button"
                icon="i-woot-bin"
                slate
                ghost
                xs
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="removePattern(index)"
              />
            </li>
          </ul>
          <div class="flex items-center gap-2">
            <div class="flex-1 min-w-0">
              <Input
                id="instruction-pattern-input"
                v-model="newPattern"
                :placeholder="
                  $t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.PLACEHOLDER')
                "
                @keydown.enter.prevent
                @enter="addPattern"
              />
            </div>
            <Button
              type="button"
              icon="i-lucide-plus"
              faded
              slate
              sm
              :label="$t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.ADD')"
              @click="addPattern"
            />
          </div>
          <Input
            id="instruction-pattern-test"
            v-model="testText"
            :label="$t('AI_AGENT.HANDOFF.INSTRUCTION_PATTERNS.TEST_LABEL')"
            @keydown.enter.prevent
          />
        </div>
      </template>
    </ConfigForm>

    <TransferTab :bot="bot" />
  </div>
</template>
